// src/components/common/Navbar.jsx
import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useCart } from '@context/CartContext';
import { getUserLanguage } from '../../utils/language';
import { IMAGE_BASE_URL } from '../../config';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { cart } = useCart();
  const language = getUserLanguage();
  const location = useLocation();

  // Dynamické texty podle jazyka
  const texts = {
    cs: {
      team: 'Team',
      cars: 'Auta',
      events: 'Events',
      gallery: 'Galerie',
      sponsors: 'Sponzoři',
      shop: 'E-shop',
      cart: 'Košík',
      driftTaxi: 'Drift Taxi',
      service: 'Servis',
      contact: 'Kontakt',
    },
    en: {
      team: 'Team',
      cars: 'Cars',
      events: 'Events',
      gallery: 'Gallery',
      sponsors: 'Sponsors',
      shop: 'Shop',
      cart: 'Cart',
      driftTaxi: 'Drift Taxi',
      service: 'Service',
      contact: 'Contact',
    },
  };

  const currentText = texts[language] || texts.en; // Fallback na angličtinu

  // Efekt pro posun na sekci po změně routy
  useEffect(() => {
    const hash = location.hash; // Např. "#sponsors"
    if (hash && location.pathname === '/') {
      const sectionId = hash.replace('#', '');
      // Malé zpoždění pro zajištění načtení komponent
      const timer = setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location]);

  // Efekt pro změnu stylu při scrollování
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const toggleMobileMenu = () => setMobileMenuOpen((prev) => !prev);

  return (
    <nav id="navbar" className={`navbar fixed w-full z-50 transition-all duration-300 ${scrolled ? 'scrolled' : ''}`}>
      <div className="container mx-auto px-4 sm:px-6 py-3">
        <div className="flex justify-between items-center">
          <Link to="/#home" className="logo-hover flex-shrink-0">
          <img
              src={`${IMAGE_BASE_URL}/images/logo-A2.png`}
              alt="NO RISK NO DRIFT Logo"
              className="h-10 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center space-x-6">
            <Link to="/#team" className="nav-link text-white hover:text-red-500 transition-colors">
              {currentText.team}
            </Link>
            <Link to="/#cars" className="nav-link text-white hover:text-red-500 transition-colors">
              {currentText.cars}
            </Link>
            <Link to="/#events" className="nav-link text-white hover:text-red-500 transition-colors">
              {currentText.events}
            </Link>
            <Link to="/#gallery" className="nav-link text-white hover:text-red-500 transition-colors">
              {currentText.gallery}
            </Link>
            <Link to="/#sponsors" className="nav-link text-white hover:text-red-500 transition-colors">
              {currentText.sponsors}
            </Link>
            <Link to="/#drift-taxi" className="nav-link text-white hover:text-red-500 transition-colors">
              {currentText.driftTaxi}
            </Link>
            <Link to="/#shop" className="nav-link text-white hover:text-red-500 transition-colors">
              {currentText.shop}
            </Link>
            <a
              href="https://noriskservis.cz"
              className="nav-link text-white hover:text-red-500 transition-colors"
              target="_blank"
              rel="noopener noreferrer"
            >
              {currentText.service}
            </a>
            <Link to="/#contact" className="nav-link text-white hover:text-red-500 transition-colors">
              {currentText.contact}
            </Link>
            <Link to="/cart" className="btn-drift relative flex items-center">
              <i className="fas fa-shopping-cart text-xs mr-2"></i>
              <span>{currentText.cart}</span>
              {cart.length > 0 && (
                <span className="ml-2 inline-flex items-center justify-center bg-white text-red-600 rounded-full px-2 py-0.5 text-xs font-bold">
                  {cart.length}
                </span>
              )}
            </Link>
          </div>

          <div className="md:hidden">
            <button onClick={toggleMobileMenu} className="p-2 focus:outline-none" aria-label="Menu">
              <div className="w-6 h-4 flex flex-col justify-between">
                <span
                  className={`w-full h-0.5 bg-white transition-all duration-300 ${
                    mobileMenuOpen ? 'transform rotate-45 translate-y-1.5' : ''
                  }`}
                ></span>
                <span
                  className={`w-full h-0.5 bg-white transition-all duration-300 ${mobileMenuOpen ? 'opacity-0' : ''}`}
                ></span>
                <span
                  className={`w-full h-0.5 bg-white transition-all duration-300 ${
                    mobileMenuOpen ? 'transform -rotate-45 -translate-y-1.5' : ''
                  }`}
                ></span>
              </div>
            </button>
          </div>
        </div>
      </div>

      <div
        className={`md:hidden ${mobileMenuOpen ? 'block' : 'hidden'} bg-black bg-opacity-95 w-full border-t border-gray-800`}
      >
        <div className="container mx-auto px-4 py-4 space-y-3">
          <Link
            to="/#team"
            className="block py-2 text-white hover:text-red-500 transition-colors"
            onClick={toggleMobileMenu}
          >
            {currentText.team}
          </Link>
          <Link
            to="/#cars"
            className="block py-2 text-white hover:text-red-500 transition-colors"
            onClick={toggleMobileMenu}
          >
            {currentText.cars}
          </Link>
          <Link
            to="/#events"
            className="block py-2 text-white hover:text-red-500 transition-colors"
            onClick={toggleMobileMenu}
          >
            {currentText.events}
          </Link>
          <Link
            to="/#gallery"
            className="block py-2 text-white hover:text-red-500 transition-colors"
            onClick={toggleMobileMenu}
          >
            {currentText.gallery}
          </Link>
          <Link
            to="/#sponsors"
            className="block py-2 text-white hover:text-red-500 transition-colors"
            onClick={toggleMobileMenu}
          >
            {currentText.sponsors}
          </Link>
          <Link
            to="/#drift-taxi"
            className="block py-2 text-white hover:text-red-500 transition-colors"
            onClick={toggleMobileMenu}
          >
            {currentText.driftTaxi}
          </Link>
          <Link
            to="/#shop"
            className="block py-2 text-white hover:text-red-500 transition-colors"
            onClick={toggleMobileMenu}
          >
            {currentText.shop}
          </Link>
          <a
            href="https://noriskservis.cz"
            className="block py-2 text-white hover:text-red-500 transition-colors"
            target="_blank"
            rel="noopener noreferrer"
            onClick={toggleMobileMenu}
          >
            {currentText.service}
          </a>
          <Link
            to="/#contact"
            className="block py-2 text-white hover:text-red-500 transition-colors"
            onClick={toggleMobileMenu}
          >
            {currentText.contact}
          </Link>
          <Link
            to="/cart"
            className="btn-drift mt-3 w-full flex items-center justify-center"
            onClick={toggleMobileMenu}
          >
            <i className="fas fa-shopping-cart text-xs mr-2"></i>
            <span>{currentText.cart}</span>
            {cart.length > 0 && (
              <span className="ml-2 inline-flex items-center justify-center bg-white text-red-600 rounded-full px-2 py-0.5 text-xs font-bold">
                {cart.length}
              </span>
            )}
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;